import {
  IDashboardFilters,
  IDashboardSummary,
  IShipmentByStatus,
  IShipmentByType,
  ITopMerchant,
  IOperatorPerformance,
  ITimingSummary,
  ISlaTrendItem,
  IServiceQuality,
  ICostSummary,
  ICostByOperator,
  ICostTrendItem,
  IGeographyItem,
  IDashboardFilterOptions,
} from "../interfaces/dashboard";
import { GET } from "../utils/constants";
import { fetchData } from "../utils/dataProvider";

const BASE = "/metrics/dashboard";

const buildUrl = (
  path: string,
  filters: IDashboardFilters,
  extra?: Record<string, string | undefined>,
): string => {
  const params = new URLSearchParams();
  params.set("from", filters.from);
  params.set("to", filters.to);
  if (filters.carrier_id) params.set("carrier_id", filters.carrier_id);
  if (filters.status) params.set("status", filters.status);
  if (filters.province) params.set("province", filters.province);
  if (filters.merchant_id) params.set("merchant_id", filters.merchant_id);
  if (filters.client_id) params.set("client_id", filters.client_id);
  if (filters.marketplaceId) params.set("marketplaceId", filters.marketplaceId);
  if (filters.country) params.set("country", filters.country);
  if (extra) {
    Object.entries(extra).forEach(([k, v]) => {
      if (v !== undefined) params.set(k, v);
    });
  }
  return `${BASE}${path}?${params.toString()}`;
};

export const fetchDashboardSummary = (
  filters: IDashboardFilters,
): Promise<IDashboardSummary> =>
  fetchData({ url: buildUrl("/summary", filters), method: GET });

export const fetchShipmentsByStatus = (
  filters: IDashboardFilters,
): Promise<IShipmentByStatus[]> =>
  fetchData({ url: buildUrl("/shipments-by-status", filters), method: GET });

export const fetchShipmentsByType = (
  filters: IDashboardFilters,
): Promise<IShipmentByType[]> =>
  fetchData({ url: buildUrl("/shipments-by-type", filters), method: GET });

export const fetchTopMerchants = (
  filters: IDashboardFilters,
  limit?: number,
): Promise<ITopMerchant[]> =>
  fetchData({
    url: buildUrl(
      "/top-merchants",
      filters,
      limit ? { limit: String(limit) } : undefined,
    ),
    method: GET,
  });

export const fetchOperatorsPerformance = (
  filters: IDashboardFilters,
): Promise<IOperatorPerformance[]> =>
  fetchData({ url: buildUrl("/operators-performance", filters), method: GET });

export const fetchTimingSummary = (
  filters: IDashboardFilters,
): Promise<ITimingSummary> =>
  fetchData({ url: buildUrl("/timing-summary", filters), method: GET });

export const fetchSlaTrend = (
  filters: IDashboardFilters,
): Promise<ISlaTrendItem[]> =>
  fetchData({ url: buildUrl("/sla-trend", filters), method: GET });

export const fetchServiceQuality = (
  filters: IDashboardFilters,
): Promise<IServiceQuality> =>
  fetchData({ url: buildUrl("/service-quality", filters), method: GET });

export const fetchCostSummary = (
  filters: IDashboardFilters,
): Promise<ICostSummary> =>
  fetchData({ url: buildUrl("/cost-summary", filters), method: GET });

export const fetchCostByOperator = (
  filters: IDashboardFilters,
): Promise<ICostByOperator[]> =>
  fetchData({ url: buildUrl("/cost-by-operator", filters), method: GET });

export const fetchCostTrend = (
  filters: IDashboardFilters,
  weeks?: number,
): Promise<ICostTrendItem[]> =>
  fetchData({
    url: buildUrl(
      "/cost-trend",
      filters,
      weeks ? { weeks: String(weeks) } : undefined,
    ),
    method: GET,
  });

export const fetchGeography = (
  filters: IDashboardFilters,
): Promise<IGeographyItem[]> =>
  fetchData({ url: buildUrl("/geography", filters), method: GET });

// Las opciones reflejan el universo del período DENTRO del scope activo: se
// envían los filtros base (fechas + marketplace + client_id). client_id define el
// scope del usuario interno (no es una dimensión), así que las opciones deben
// acotarse al cliente elegido. Se siguen omitiendo las dimensiones ya
// seleccionadas (carrier/status/province/merchant) — Opción A.
export const fetchDashboardFilterOptions = (
  filters: IDashboardFilters,
): Promise<IDashboardFilterOptions> =>
  fetchData({
    url: buildUrl("/filter-options", {
      from: filters.from,
      to: filters.to,
      marketplaceId: filters.marketplaceId,
      client_id: filters.client_id,
      country: filters.country,
    }),
    method: GET,
  });
